import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import IconButton from '../../IconButton';
import PopupMenu from '../../PopupMenu';
import PrimaryButton from '../../PrimaryButton';
import CheckIcon from '../../../../public/assets/images/theme-2/icons/handin-hover.svg';
import { ViewSection, LockInfo } from '../styles';
import {
  SubmitPanel,
  Glossary,
  GlossaryHeader,
  GlossaryBody,
  GlossaryRow,
  GlossaryConcept,
  GlossaryDescription,
  GlossarySelector,
  QuizInfo,
  ResultInfo,
} from '../../NodeContentPreview/styles';

const CorrelationContentViewer = ({ data, report, saveReport, isTeacher, theme }) => {
  const [concepts, setConcepts] = useState([]);
  const [descriptions, setDescriptions] = useState([]);
  const [redoable, setRedoable] = useState(false);
  const [locked, setLocked] = useState(false);
  const [doSave, setDoSave] = useState(false);
  const [done, setDone] = useState(false);
  const [menuToggle, setMenuToggle] = useState(-1);
  const [menuPopup, setMenuPopup] = useState();
  const [selectedTerms, setSelectedTerms] = useState({});
  const [corrected, setCorrected] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);

  const isEditable = !isTeacher && !locked && (redoable || !corrected);
  const conceptCount = concepts?.length || 0;

  const shuffleArray = (array) => {
    for (let i = array.length - 1; i >= 0; i--) {
      const j = Math.floor(Math.random() * array.length);
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  const resetMenuToggle = () => {
    setMenuToggle(-1);
  };

  const getAnswers = (concepts) => {
    const answers = report?.CorrelationAnswers;
    const getAnswerId = (conceptId) =>
      (answers || []).find((answer) => answer.correlation_concept_id === conceptId)?.correlation_description_id;
    return concepts.reduce((acc, curr) => ((acc[curr.id] = getAnswerId(curr.id)), acc), {});
  };

  useEffect(() => {
    if (corrected && doSave) {
      saveReport({ ...report, state: 'accept', answers: selectedTerms });
    }
  }, [corrected, selectedTerms, doSave]);

  useEffect(() => {
    const selectedValues = Object.values(selectedTerms).filter((termId) => !!termId);
    if (selectedValues.length === 0) {
      return;
    }
    if (selectedValues.length === concepts.length) {
      setDone(true);
    } else {
      setDone(false);
    }
  }, [selectedTerms, concepts]);

  useEffect(() => {
    if (!isTeacher && done) {
      correctQuiz(getAnswers(concepts));
    }
  }, [concepts]);

  useEffect(() => {
    const newConcepts = data?.CorrelationList?.Concepts || [];
    setConcepts(newConcepts);
    const newDescriptions = newConcepts.map((concept) => ({
      id: concept.Description.id,
      body: concept.Description.body,
    }));
    setDescriptions(shuffleArray(newDescriptions));
    setRedoable(data.redoable);
    setLocked(report?.locked);
    setCorrected(report?.state === 'done' || report?.state === 'accepted');
    setSelectedTerms(getAnswers(newConcepts));
  }, []);

  useEffect(() => {
    setMenuPopup(getMenuPopup());
  }, [menuToggle]);

  const selectOption = (termId) => {
    const key = Object.keys(selectedTerms).find((key) => selectedTerms[key] === termId);
    if (key) {
      delete selectedTerms[key];
    }
    setSelectedTerms({ ...selectedTerms, [menuToggle]: termId });
    resetMenuToggle();
  };

  const getMenuOptions = () => {
    return (descriptions || []).map((description) => ({
      tag: description.id,
      name: description.body,
      selected: Object.values(selectedTerms).includes(description.id),
      onSelect: (termId) => selectOption(termId),
    }));
  };

  const getMenuPopup = () => {
    return (
      <PopupMenu
        id={`conceptPopup_${menuToggle}`}
        popupIsOpen={menuToggle !== -1}
        onClose={resetMenuToggle}
        options={getMenuOptions()}
        lightMode={true}
        alignment="left"
        border={true}
      />
    );
  };

  const showMenu = (id) => {
    if (id === menuToggle) {
      resetMenuToggle();
    } else {
      setTimeout(() => setMenuToggle(id), 10);
    }
  };

  const correctQuiz = (oldAnswers) => {
    let correct = 0;
    concepts.forEach((concept) => {
      if ((oldAnswers || selectedTerms)[concept.id] === concept.Description.id) {
        correct++;
      }
    });
    setCorrectAnswers(correct);
    setCorrected(true);
    if (!oldAnswers) {
      setDoSave(true);
    }
  };

  const resetQuiz = () => {
    if (redoable && !locked) {
      const answers = concepts.reduce((acc, curr) => ((acc[curr.id] = undefined), acc), {});
      setSelectedTerms(answers);
      setCorrectAnswers(0);
      setCorrected(false);
      saveReport({ ...report, state: 'active', answers });
    }
  };

  return (
    <>
      <ViewSection>
        <Glossary>
          <GlossaryHeader>
            <GlossaryConcept>{i18n.__('correlation_term')}</GlossaryConcept>
            <GlossaryDescription>{i18n.__('correlation_description')}</GlossaryDescription>
          </GlossaryHeader>
          <GlossaryBody>
            {concepts.map((concept, index) => {
              const { id, body, Description } = concept;
              const term = descriptions.find((term) => term.id === selectedTerms[id]);
              const className = selectedTerms[id] === Description.id ? 'correct' : 'error';
              return (
                <GlossaryRow key={id} even={index % 2}>
                  <GlossaryConcept dangerouslySetInnerHTML={{ __html: body }} />
                  <GlossarySelector
                    title={i18n.__('correlation_no_desc_chosen')}
                    onClick={() => showMenu(id)}
                    id={`conceptPopup_${id}`}
                    disabled={!isEditable || locked || corrected}
                    className={corrected ? className : ''}
                    dangerouslySetInnerHTML={{ __html: term ? term.body : i18n.__('correlation_no_desc_chosen') }}
                  />
                </GlossaryRow>
              );
            })}
            {menuPopup}
          </GlossaryBody>
        </Glossary>
        {corrected && <ResultInfo>{i18n.__('quiz_correcting_result', correctAnswers, conceptCount)}</ResultInfo>}
        <QuizInfo>{i18n.__(redoable ? 'correlation_redoable_desc' : 'correlation_not_redoable_desc')}</QuizInfo>
      </ViewSection>
      {!isTeacher && (
        <SubmitPanel className={'ir-hide'}>
          {locked ? (
            <LockInfo>{i18n.__('editor_locked_desc')}</LockInfo>
          ) : isEditable && !corrected ? (
            <IconButton
              icon={CheckIcon}
              onClick={() => correctQuiz()}
              disabled={!done || locked}
              backgroundColor={theme.colors.buttonGreenFront}
            >
              {i18n.__('app_correct')}
            </IconButton>
          ) : (
            <PrimaryButton
              disabled={!redoable || locked}
              onClick={resetQuiz}
              text={i18n.__('app_reset')}
              className="reset"
            />
          )}
        </SubmitPanel>
      )}
    </>
  );
};

export default withTheme(CorrelationContentViewer);

CorrelationContentViewer.propTypes = {
  data: PropTypes.object.isRequired,
  report: PropTypes.object,
  saveReport: PropTypes.func.isRequired,
  isTeacher: PropTypes.bool.isRequired,
};
